import { useState } from "react";
import { Link } from "react-router-dom";
import { Segmented } from "antd";
import type { TasksByProjectRow } from "../../../queries/reports";
import { routes } from "../../../navigation/routes/routes";
import { DashCard } from "./DashCard";
import { Legend, StackedBar } from "./StackedBar";
import {
  PRIORITY_COLOR, PRIORITY_LABEL, PRIORITY_ORDER, SEVERITY_COLOR, SEVERITY_LABEL, SEVERITY_ORDER,
  STATUS_COLOR, STATUS_LABEL, STATUS_ORDER, buildSegments, sumCounts,
} from "./format";
import styles from "./dash.module.css";

type Mode = "status" | "severity" | "priority";

const MODES: Record<Mode, {
  order: readonly string[]; labels: Record<string, string>; colors: Record<string, string>;
  counts: (r: TasksByProjectRow) => Partial<Record<string, number>>;
}> = {
  status: { order: STATUS_ORDER, labels: STATUS_LABEL, colors: STATUS_COLOR, counts: (r) => r.byStatus },
  severity: { order: SEVERITY_ORDER, labels: SEVERITY_LABEL, colors: SEVERITY_COLOR, counts: (r) => r.bySeverity },
  priority: { order: PRIORITY_ORDER, labels: PRIORITY_LABEL, colors: PRIORITY_COLOR, counts: (r) => r.byPriority },
};

interface Props {
  rows?: TasksByProjectRow[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

export const TasksByProjectSection = ({ rows, loading, error, onRetry }: Props) => {
  const [mode, setMode] = useState<Mode>("status");
  const list = rows || [];
  const cfg = MODES[mode];
  const max = Math.max(0, ...list.map((r) => r.total));
  return (
    <DashCard
      title="Tasks by project"
      helper="Task counts per project. Switch the colouring to see severity or priority instead of status."
      controls={
        <Segmented
          aria-label="Colour bars by"
          value={mode}
          onChange={(v) => setMode(v as Mode)}
          options={[
            { label: "Status", value: "status" },
            { label: "Severity", value: "severity" },
            { label: "Priority", value: "priority" },
          ]}
          className={styles.segmented}
        />
      }
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={list.length === 0}
      emptyTitle="No tasks yet"
      emptyText="Tasks appear here once they are created in a project."
    >
      <Legend items={cfg.order.map((k) => ({ key: k, label: cfg.labels[k], color: cfg.colors[k] }))} />
      <ul className={styles.plainList}>
        {list.map((r) => (
          <li key={r.projectId} className={styles.barRow}>
            <div className={styles.barRowHead}>
              <Link className={styles.link} to={routes.projectBoard.replace(":id", r.projectId)}>{r.name}</Link>
              <span className={`${styles.muted} osi-num`}>
                {sumCounts(cfg.counts(r))} tasks · Backlog {r.backlog} · In active sprint {r.inActiveSprint}
              </span>
            </div>
            <StackedBar
              segments={buildSegments(cfg.counts(r), cfg.order, cfg.labels, cfg.colors)}
              max={max}
              label={`${r.name} tasks by ${mode}`}
            />
          </li>
        ))}
      </ul>
    </DashCard>
  );
};
